import PasswordGate from '../components/PasswordGate';
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';

const DEPARTMENTS = ['Management','Development','Design','Marketing','Sales','Support','Finance','HR','Other'];
const EMP_TYPES   = ['On Site','Remote','Hybrid'];
const STATUSES    = ['Active','Inactive','On Leave'];
const DEPT_BADGE  = { Management:'badge-purple', Development:'badge-info', Design:'badge-green', Marketing:'badge-warn', Sales:'badge-green', Support:'badge-muted', Finance:'badge-warn', HR:'badge-info', Other:'badge-muted' };
const TYPE_BADGE  = { 'On Site':'badge-green', Remote:'badge-info', Hybrid:'badge-warn' };
const EMPTY = { name:'', employee_id:'', email:'', phone:'', civil_id:'', passport_number:'', nationality:'', department:'Development', employment_type:'On Site', position:'', salary:'', join_date:'', status:'Active', notes:'' };

function printPayroll(employees) {
  const win = window.open('', '_blank');
  const totalSalary = employees.reduce((s,e)=>s+parseFloat(e.salary||0),0);
  const rows = employees.map(e=>'<tr><td>'+e.employee_id+'</td><td style="font-weight:600">'+e.name+'</td><td>'+e.nationality+'</td><td>'+e.position+'</td><td>'+e.department+'</td><td>'+e.employment_type+'</td><td style="font-weight:700;color:#166534">KWD '+parseFloat(e.salary||0).toFixed(3)+'</td><td>'+e.status+'</td></tr>').join('');
  win.document.write('<!DOCTYPE html><html><head><title>Payroll Report</title><style>body{font-family:Arial,sans-serif;padding:30px;color:#111}.header{display:flex;justify-content:space-between;border-bottom:2px solid #00b368;padding-bottom:16px;margin-bottom:20px}.company{font-size:22px;font-weight:700;color:#00b368}.stats{display:flex;gap:20px;margin-bottom:20px}.stat{background:#f5f5f5;padding:12px 20px;border-radius:8px}.stat-label{font-size:11px;color:#888}.stat-val{font-size:18px;font-weight:700;margin-top:2px}table{width:100%;border-collapse:collapse;font-size:13px}th{background:#f5f5f5;padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;border-bottom:2px solid #eee;color:#666}td{padding:10px 12px;border-bottom:1px solid #f0f0f0}.total-row{font-weight:700;background:#f0fdf4}.footer{margin-top:30px;text-align:center;font-size:11px;color:#aaa;border-top:1px solid #eee;padding-top:12px}</style></head><body><img src="https://teknulugy.online/logo.svg" style="height:50px;display:block;margin:0 auto 10px auto"><div class="header"><div><div class="company">TEKNULUGY</div><div style="font-size:12px;color:#666">IT Services - Kuwait</div></div><div style="text-align:right"><div style="font-size:18px;font-weight:700">Payroll Report</div><div style="font-size:12px;color:#666">Generated: '+new Date().toLocaleDateString()+'</div></div></div><div class="stats"><div class="stat"><div class="stat-label">Total Employees</div><div class="stat-val">'+employees.length+'</div></div><div class="stat"><div class="stat-label">Active</div><div class="stat-val" style="color:#166534">'+employees.filter(e=>e.status==='Active').length+'</div></div><div class="stat"><div class="stat-label">Monthly Payroll</div><div class="stat-val" style="color:#166534">KWD '+totalSalary.toFixed(3)+'</div></div><div class="stat"><div class="stat-label">Remote</div><div class="stat-val" style="color:#1e40af">'+employees.filter(e=>e.employment_type==='Remote').length+'</div></div></div><table><thead><tr><th>ID</th><th>Name</th><th>Nationality</th><th>Position</th><th>Department</th><th>Type</th><th>Salary</th><th>Status</th></tr></thead><tbody>'+rows+'</tbody><tfoot><tr class="total-row"><td colspan="6">TOTAL PAYROLL</td><td>KWD '+totalSalary.toFixed(3)+'</td><td></td></tr></tfoot></table><div class="footer">Teknulugy IT Services - Ahmed Al Jaber Street, Baitak Tower, Floor 1, Office D - +965 55107000</div></body></html>');
  win.document.close();
  setTimeout(()=>win.print(),500);
}

export default function PayrollPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [workPermitFile, setWorkPermitFile] = useState(null);
  const [deptFilter, setDeptFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const { data, isLoading } = useQuery({
    queryKey:['employees',deptFilter,typeFilter],
    queryFn:()=>api.get('/employees',{params:{department:deptFilter||undefined,employment_type:typeFilter||undefined}}).then(r=>r.data),
  });

  const readFile = file => new Promise(resolve => {
    const r = new FileReader();
    r.onload = e => resolve(e.target.result);
    r.readAsDataURL(file);
  });

  const createMut = useMutation({
    mutationFn: async data => {
      if (workPermitFile) { data.work_permit_file = await readFile(workPermitFile); data.work_permit_filename = workPermitFile.name; }
      return api.post('/employees', data);
    },
    onSuccess:()=>{ qc.invalidateQueries(['employees']); setShowForm(false); setForm(EMPTY); setWorkPermitFile(null); toast.success('Employee added'); },
    onError: e=>toast.error(e.response?.data?.message||'Error'),
  });

  const updateMut = useMutation({
    mutationFn: async ({id,data}) => {
      if (workPermitFile) { data.work_permit_file = await readFile(workPermitFile); data.work_permit_filename = workPermitFile.name; }
      return api.put('/employees/'+id, data);
    },
    onSuccess:()=>{ qc.invalidateQueries(['employees']); setEditing(null); setWorkPermitFile(null); toast.success('Updated'); },
  });

  const deleteMut = useMutation({
    mutationFn: id=>api.delete('/employees/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['employees']); toast.success('Removed'); },
  });

  const downloadPermit = async (id, name) => {
    try {
      const res = await api.get('/employees/'+id+'/work-permit', { responseType:'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href=url; a.download=name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('File not found'); }
  };

  const employees = data?.data || [];
  const totalSalary = employees.filter(e=>e.status==='Active').reduce((s,e)=>s+parseFloat(e.salary||0),0);

  const handleSave = () => {
    if (!form.name) return toast.error('Name is required');
    const payload = {...form, salary:parseFloat(form.salary)||0};
    editing ? updateMut.mutate({id:editing.id,data:payload}) : createMut.mutate(payload);
  };

  const openEdit = e => {
    setEditing(e);
    setForm({ name:e.name, employee_id:e.employee_id||'', email:e.email||'', phone:e.phone||'',
      civil_id:e.civil_id||'', passport_number:e.passport_number||'', nationality:e.nationality||'',
      department:e.department, employment_type:e.employment_type, position:e.position||'',
      salary:e.salary, join_date:e.join_date||'', status:e.status, notes:e.notes||'' });
    setWorkPermitFile(null);
    setShowForm(false);
  };

  return (
    <PasswordGate moduleName="payroll" moduleLabel="Payroll">
    <div>
      <div className="g4" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Employees</div><div className="stat-val">{employees.length}</div></div>
        <div className="card-sm"><div className="stat-label">Active</div><div className="stat-val" style={{color:'var(--green)'}}>{employees.filter(e=>e.status==='Active').length}</div></div>
        <div className="card-sm"><div className="stat-label">Remote</div><div className="stat-val" style={{color:'var(--info)'}}>{employees.filter(e=>e.employment_type==='Remote').length}</div></div>
        <div className="card-sm"><div className="stat-label">Monthly Payroll</div><div className="stat-val" style={{color:'var(--green)'}}>KWD {totalSalary.toFixed(3)}</div></div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <select className="form-select" style={{width:150}} value={deptFilter} onChange={e=>setDeptFilter(e.target.value)}>
          <option value="">All Departments</option>
          {DEPARTMENTS.map(d=><option key={d}>{d}</option>)}
        </select>
        <select className="form-select" style={{width:130}} value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {EMP_TYPES.map(t=><option key={t}>{t}</option>)}
        </select>
        {(deptFilter||typeFilter)&&<button className="btn btn-outline btn-sm" onClick={()=>{setDeptFilter('');setTypeFilter('');}}>Clear</button>}
        <div style={{marginLeft:'auto',display:'flex',gap:8}}>
          <button className="btn btn-outline" onClick={()=>printPayroll(employees)} style={{display:'flex',alignItems:'center',gap:6}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print Payroll
          </button>
          <button className="btn btn-primary" onClick={()=>{setShowForm(v=>!v);setEditing(null);setForm(EMPTY);setWorkPermitFile(null);}}>+ Add Employee</button>
        </div>
      </div>

      {(showForm||editing) && (
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">{editing?'Edit Employee':'New Employee'}</div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Full Name *</label>
              <input className="form-input" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="Employee full name"/></div>
            <div className="form-group"><label className="form-label">Employee ID</label>
              <input className="form-input" value={form.employee_id} onChange={e=>set('employee_id',e.target.value)} placeholder="Auto-generated if empty"/></div>
            <div className="form-group"><label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="email@company.com"/></div>
            <div className="form-group"><label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="+965 XXXX XXXX"/></div>
            <div className="form-group"><label className="form-label">Civil ID</label>
              <input className="form-input" value={form.civil_id} onChange={e=>set('civil_id',e.target.value)} placeholder="Civil ID number"/></div>
            <div className="form-group"><label className="form-label">Passport Number</label>
              <input className="form-input" value={form.passport_number} onChange={e=>set('passport_number',e.target.value)} placeholder="Passport number"/></div>
            <div className="form-group"><label className="form-label">Nationality</label>
              <input className="form-input" value={form.nationality} onChange={e=>set('nationality',e.target.value)} placeholder="e.g. Kuwaiti, Egyptian..."/></div>
            <div className="form-group"><label className="form-label">Position</label>
              <input className="form-input" value={form.position} onChange={e=>set('position',e.target.value)} placeholder="e.g. Senior Developer"/></div>
            <div className="form-group"><label className="form-label">Department</label>
              <select className="form-select" value={form.department} onChange={e=>set('department',e.target.value)}>
                {DEPARTMENTS.map(d=><option key={d}>{d}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Employment Type</label>
              <select className="form-select" value={form.employment_type} onChange={e=>set('employment_type',e.target.value)}>
                {EMP_TYPES.map(t=><option key={t}>{t}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e=>set('status',e.target.value)}>
                {STATUSES.map(s=><option key={s}>{s}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Monthly Salary (KWD)</label>
              <input className="form-input" type="number" value={form.salary} onChange={e=>set('salary',e.target.value)} placeholder="0.000"/></div>
            <div className="form-group"><label className="form-label">Join Date</label>
              <input className="form-input" type="date" value={form.join_date} onChange={e=>set('join_date',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Work Permit Document</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e=>setWorkPermitFile(e.target.files[0])}
                style={{padding:'8px 12px',background:'#fff',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)',width:'100%',fontSize:13}}/>
              {workPermitFile&&<div style={{fontSize:11,color:'var(--green)',marginTop:4}}>Selected: {workPermitFile.name}</div>}
              {editing&&editing.work_permit_name&&!workPermitFile&&<div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>Current: {editing.work_permit_name}</div>}
            </div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Notes..."/></div>
          </div>
          <div style={{display:'flex',gap:8,marginTop:4}}>
            <button className="btn btn-primary" onClick={handleSave}>Save Employee</button>
            <button className="btn btn-outline" onClick={()=>{setShowForm(false);setEditing(null);setWorkPermitFile(null);}}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
          <table className="tbl">
            <thead><tr><th>Employee</th><th>ID</th><th>Civil ID</th><th>Passport</th><th>Nationality</th><th>Position</th><th>Dept</th><th>Type</th><th>Salary</th><th>Work Permit</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {employees.length===0&&<tr><td colSpan={12} className="empty-state">No employees found</td></tr>}
              {employees.map(e=>(
                <tr key={e.id}>
                  <td><div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div className="avatar">{e.name.slice(0,2).toUpperCase()}</div>
                    <div><div style={{fontWeight:600}}>{e.name}</div><div style={{fontSize:12,color:'var(--muted)'}}>{e.email||'--'}</div></div>
                  </div></td>
                  <td style={{fontFamily:'monospace',fontSize:12,color:'var(--muted)'}}>{e.employee_id||'--'}</td>
                  <td style={{fontSize:12,color:'var(--muted)'}}>{e.civil_id||'--'}</td>
                  <td style={{fontSize:12,color:'var(--muted)'}}>{e.passport_number||'--'}</td>
                  <td style={{fontSize:12}}>{e.nationality||'--'}</td>
                  <td style={{fontWeight:500}}>{e.position||'--'}</td>
                  <td><span className={"badge "+(DEPT_BADGE[e.department]||'badge-muted')} style={{fontSize:10}}>{e.department}</span></td>
                  <td><span className={"badge "+(TYPE_BADGE[e.employment_type]||'badge-muted')} style={{fontSize:10}}>{e.employment_type}</span></td>
                  <td style={{fontWeight:700,color:'var(--green)'}}>KWD {parseFloat(e.salary||0).toFixed(3)}</td>
                  <td>{e.work_permit_name
                    ?<button className="btn btn-outline btn-sm" style={{color:'var(--green)',borderColor:'var(--green)'}} onClick={()=>downloadPermit(e.id,e.work_permit_name)}>Download</button>
                    :<span style={{color:'#ccc',fontSize:12}}>--</span>}
                  </td>
                  <td><span className={"badge "+(e.status==='Active'?'badge-green':e.status==='On Leave'?'badge-warn':'badge-danger')}>{e.status}</span></td>
                  <td><div style={{display:'flex',gap:6}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>openEdit(e)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Delete?'))deleteMut.mutate(e.id);}}>X</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </PasswordGate>
  );
}
